import React, { useContext, useEffect, useMemo, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  MonitorCog,
  Plus,
  RefreshCcw,
  Search,
  PlugZap,
  WalletCards,
} from "lucide-react";

import { GlobalContext } from "../../../../Context/GlobalContext";
import useIntegration from "../../../../services/Settings/useIntegration";

import TerminalTable from "./IntegrationComponents/TerminalTable";
import TerminalFormModal from "./IntegrationComponents/TerminalFormModal";
import DrawerFormModal from "./IntegrationComponents/DrawerFormModal";
import TerminalDetailsModal from "./IntegrationComponents/TerminalDetailsModal";
import LoadingScreen from "../../../../utils/LoadingScreen";
import usePosOrders from "../../../../services/Pos/usePosOrders";

const Integration = () => {
  const { userID } = useContext(GlobalContext);

  const { getPosTerminal, insertPosTerminal, getPosDrawer, insertPosDrawer } =
    useIntegration();

  const {
    posButtonsMaster,
    posButtonsMasterLoading,
    orderOptions,
    orderOptionsLoading,
    insertPosOptionsLoading,
    getPosButtonsMaster,
    getPosOrderOptions,
    insertPosOrderOptions,
  } = usePosOrders();

  const [terminals, setTerminals] = useState([]);
  const [drawers, setDrawers] = useState([]);

  const [loading, setLoading] = useState(false);
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [search, setSearch] = useState("");

  const [terminalModalOpen, setTerminalModalOpen] = useState(false);
  const [drawerModalOpen, setDrawerModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);

  const [selectedTerminal, setSelectedTerminal] = useState(null);
  const [selectedPosOptions, setSelectedPosOptions] = useState([]);

  const [terminalForm, setTerminalForm] = useState({
    terminalname: "",
    terminalcode: "",
    deviceid: "",
  });

  const [drawerForm, setDrawerForm] = useState({
    drawerName: "",
    drawerCode: "",
    deviceId: "",
  });

  const normalizePosOptionValue = (value = "") => {
  const normalized = String(value)
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");

  if (["dining", "dinein", "dine_in"].includes(normalized)) {
    return "dinein";
  }

  if (
    ["takeaway", "takeway", "take_away", "takwaway"].includes(normalized)
  ) {
    return "takeaway";
  }

  if (["home_delivery", "homedelivery"].includes(normalized)) {
    return "homedelivery";
  }

  if (["room_order", "roomorder"].includes(normalized)) {
    return "room_order";
  }

  return normalized;
};

  const normalizePosOption = (option = {}) => {
  const rawValue =
    option?.value ||
    option?.button_value ||
    option?.type ||
    option?.orderType ||
    option?.config_value ||
    "";

  return {
    label:
      option?.label ||
      option?.button_name ||
      option?.name ||
      option?.config_name ||
      rawValue ||
      "",

    value: normalizePosOptionValue(rawValue),

    status: String(
      option?.status ??
        option?.isActive ??
        option?.active ??
        option?.enabled ??
        "N",
    )
      .trim()
      .toUpperCase(),
  };
};

  // ================= FETCH TERMINALS =================
  const fetchTerminals = async () => {
    try {
      setLoading(true);

      const res = await getPosTerminal({
        uscode: userID,
      });

      if (res?.responseCode === "00") {
        setTerminals(res?.response || []);
      } else {
        setTerminals([]);
        toast.error(res?.responseMessage || "Failed to fetch terminals");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching terminals");
    } finally {
      setLoading(false);
    }
  };

  // ================= FETCH DRAWERS =================
  const fetchDrawersByTerminal = async (terminal) => {
    try {
      if (!terminal?.pk_terminal_id) return;

      setDrawerLoading(true);

      const res = await getPosDrawer({
        uscode: userID,
        terminalid: String(terminal.pk_terminal_id),
      });

      if (res?.responseCode === "00") {
        setDrawers(res?.response || []);
      } else {
        setDrawers([]);
      }
    } catch (error) {
      console.error(error);
      toast.error("Error fetching drawers");
      setDrawers([]);
    } finally {
      setDrawerLoading(false);
    }
  };

  useEffect(() => {
    if (!userID) return;

    const loadIntegrationData = async () => {
      await Promise.all([
        fetchTerminals(),
        getPosButtonsMaster(),
        getPosOrderOptions(),
      ]);
    };

    loadIntegrationData();
  }, [userID]);

useEffect(() => {
  if (!Array.isArray(orderOptions)) {
    setSelectedPosOptions([]);
    return;
  }

  const directOptions = orderOptions.filter(
    (item) =>
      item?.value ||
      item?.type ||
      item?.orderType ||
      item?.config_value,
  );

  const savedOptions =
    directOptions.length > 0
      ? directOptions
      : orderOptions.flatMap((item) =>
          Array.isArray(item?.options) ? item.options : [],
        );

  const selectedValues = savedOptions
    .map(normalizePosOption)
    .filter(
      (item) =>
        item.value &&
        item.status === "Y",
    )
    .map((item) => item.value);

  console.log("ACTIVE POS OPTIONS:", selectedValues);

  setSelectedPosOptions(selectedValues);
}, [orderOptions]);

  // ================= FILTER TERMINALS =================
  const filteredTerminals = useMemo(() => {
    const value = search.toLowerCase();

    return terminals.filter((terminal) => {
      const terminalName = String(terminal.terminal_name || "").toLowerCase();
      const terminalCode = String(terminal.terminal_code || "").toLowerCase();
      const deviceId = String(terminal.device_id || "").toLowerCase();

      return (
        terminalName.includes(value) ||
        terminalCode.includes(value) ||
        deviceId.includes(value)
      );
    });
  }, [terminals, search]);

  // ================= GET DRAWERS BY TERMINAL =================
  const getTerminalDrawers = (terminalId) => {
    return drawers.filter(
      (drawer) => String(drawer.fk_terminal_id) === String(terminalId),
    );
  };

  // ================= OPEN ADD TERMINAL =================
  const openAddTerminalModal = () => {
    setTerminalForm({
      terminalname: "",
      terminalcode: "",
      deviceid: "",
    });

    setTerminalModalOpen(true);
  };

  // ================= OPEN ADD DRAWER =================
  const openAddDrawerModal = (terminal) => {
    setSelectedTerminal(terminal);

    setDrawerForm({
      drawerName: "",
      drawerCode: "",
      deviceId: terminal?.device_id || "",
    });

    setDrawerModalOpen(true);
  };

  // ================= OPEN DETAILS =================
  const openDetailsModal = (terminal) => {
    setSelectedTerminal(terminal);
    setDetailsModalOpen(true);
    fetchDrawersByTerminal(terminal);
  };

  // ================= CREATE TERMINAL =================
  const handleCreateTerminal = async () => {
    try {
      if (!terminalForm.terminalname.trim()) {
        return toast.warning("Terminal name is required");
      }

      if (!terminalForm.terminalcode.trim()) {
        return toast.warning("Terminal code is required");
      }

      if (!terminalForm.deviceid.trim()) {
        return toast.warning("Device ID is required");
      }

      const payload = {
        uscode: userID,
        terminalname: terminalForm.terminalname,
        terminalcode: terminalForm.terminalcode,
        deviceid: terminalForm.deviceid,
      };

      const res = await insertPosTerminal(payload);

      if (res?.responseCode === "00") {
        toast.success(res?.responseMessage || "Terminal created successfully");

        setTerminalModalOpen(false);
        setTerminalForm({
          terminalname: "",
          terminalcode: "",
          deviceid: "",
        });

        fetchTerminals();
      } else {
        toast.error(res?.responseMessage || "Failed to create terminal");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error creating terminal");
    }
  };

  // ================= CREATE DRAWER =================
  const handleCreateDrawer = async () => {
    try {
      if (!selectedTerminal) {
        return toast.warning("Please select terminal first");
      }

      if (!drawerForm.drawerName.trim()) {
        return toast.warning("Drawer name is required");
      }

      if (!drawerForm.drawerCode.trim()) {
        return toast.warning("Drawer code is required");
      }

      if (!drawerForm.deviceId.trim()) {
        return toast.warning("Device ID is required");
      }

      const payload = {
        uscode: userID,

        // Backend key is written as terminald in your curl, so keeping same.
        terminald: String(selectedTerminal.pk_terminal_id),

        terminalName: selectedTerminal.terminal_name,
        drawerName: drawerForm.drawerName,
        drawerCode: drawerForm.drawerCode,
        deviceId: drawerForm.deviceId,
      };

      const res = await insertPosDrawer(payload);

      if (res?.responseCode === "00") {
        toast.success(res?.responseMessage || "Drawer added successfully");

        setDrawerModalOpen(false);
        setDrawerForm({
          drawerName: "",
          drawerCode: "",
          deviceId: "",
        });

        fetchDrawersByTerminal(selectedTerminal);
      } else {
        toast.error(res?.responseMessage || "Failed to add drawer");
      }
    } catch (error) {
      console.error(error);
      toast.error("Error adding drawer");
    }
  };

  const handleTogglePosOption = (value) => {
    setSelectedPosOptions((previous) => {
      if (previous.includes(value)) {
        return previous.filter((item) => item !== value);
      }

      return [...previous, value];
    });
  };

 const handleSavePosOptions = async () => {
  const masterOptions = Array.isArray(posButtonsMaster)
    ? posButtonsMaster
    : [];

  if (masterOptions.length === 0) {
    toast.error("POS button master data not found");
    return;
  }

  const optionsPayload = masterOptions
    .map((option) => {
      const normalizedOption = normalizePosOption(option);

      const originalValue =
        option?.value ||
        option?.button_value ||
        option?.type ||
        option?.orderType ||
        option?.config_value ||
        "";

      return {
        label: normalizedOption.label,
        value: originalValue,
        status: selectedPosOptions.includes(normalizedOption.value)
          ? "Y"
          : "N",
      };
    })
    .filter((item) => item.label && item.value);

  const response = await insertPosOrderOptions(optionsPayload);

  if (response?.responseCode === "00") {
    const result = Array.isArray(response?.response)
      ? response.response[0]
      : null;

    await getPosOrderOptions();
  }
};

  return (
    <>
    <ToastContainer/>
      <div className=" min-h-screen montserrat-font-1">
        {/* HEADER */}
        <div className="mb-5 bg-white p-5 rounded-xl border shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-800 flex items-center gap-2">
                <PlugZap className="w-6 h-6 text-green-600" />
                POS Integration
              </h1>

              <p className="text-sm text-gray-500 mt-1 montserrat-font-2">
                Manage POS terminals and cash drawers attached to each terminal.
              </p>
            </div>

            <div className="hidden md:flex items-center gap-3">
              <div className="bg-green-50 border border-green-100 px-4 py-3 rounded-xl">
                <p className="text-xs text-gray-500">Total Terminals</p>
                <p className="text-xl font-semibold text-green-700">
                  {terminals.length}
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-100 px-4 py-3 rounded-xl">
                <p className="text-xs text-gray-500">Drawer Fetch</p>
                <p className="text-sm font-semibold text-blue-700">
                  Terminal Wise
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* TOP ACTION BAR */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-5">
          <div>
            <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
              <MonitorCog className="w-5 h-5 text-green-600" />
              Terminal List
            </h2>
            <p className="text-sm text-gray-500">
              View terminal details and attach drawers.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <div className="flex items-center border rounded-lg px-3 py-2 bg-white w-full sm:w-72">
              <Search size={15} className="text-gray-400 mr-2" />

              <input
                className="outline-none text-sm w-full"
                placeholder="Search terminal, code, device..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <button
              onClick={openAddTerminalModal}
              className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex gap-2 items-center justify-center text-sm font-medium"
            >
              <Plus size={16} />
              Add New Terminal
            </button>

            <button
              onClick={fetchTerminals}
              className="border px-4 py-2 rounded-lg text-white text-sm bg-green-500 hover:bg-green-600 flex gap-2 items-center justify-center"
            >
              <RefreshCcw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* MAIN CONTENT */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden">
          {loading ? (
            <div className="py-14 flex justify-center">
              <LoadingScreen text="Loading terminals..." />
            </div>
          ) : (
            <TerminalTable
              terminals={filteredTerminals}
              onView={openDetailsModal}
              onAddDrawer={openAddDrawerModal}
            />
          )}
        </div>

        {/* POS ORDER OPTIONS */}
        <div className="bg-white rounded-xl border shadow-sm p-5 mt-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <MonitorCog className="w-5 h-5 text-green-600" />
                POS Order Options
              </h2>

              <p className="text-sm text-gray-500 mt-1">
                Select which order buttons should be visible on the POS screen.
              </p>
            </div>

            <button
              type="button"
              onClick={handleSavePosOptions}
              disabled={
                insertPosOptionsLoading ||
                posButtonsMasterLoading ||
                orderOptionsLoading
              }
              className="bg-green-500 hover:bg-green-600 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-5 py-2.5 rounded-lg text-sm font-semibold"
            >
              {insertPosOptionsLoading ? "Saving..." : "Save POS Options"}
            </button>
          </div>

          {posButtonsMasterLoading || orderOptionsLoading ? (
            <div className="py-8 flex justify-center">
              <LoadingScreen text="Loading POS options..." />
            </div>
          ) : !Array.isArray(posButtonsMaster) ||
            posButtonsMaster.length === 0 ? (
            <div className="border border-dashed border-gray-300 rounded-xl p-8 text-center text-sm text-gray-500">
              No POS button master data found
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {posButtonsMaster.map((option, index) => {
                const normalizedOption = normalizePosOption(option);
                const isSelected = selectedPosOptions.includes(
                  normalizedOption.value,
                );

                return (
                  <label
                    key={normalizedOption.value || index}
                    className={`cursor-pointer border-2 rounded-xl p-4 transition-all ${
                      isSelected
                        ? "border-green-500 bg-green-50"
                        : "border-gray-200 bg-white hover:border-green-300"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() =>
                          handleTogglePosOption(normalizedOption.value)
                        }
                        className="w-5 h-5 accent-green-500"
                      />

                      <div>
                        <p className="font-semibold text-gray-800">
                          {normalizedOption.label || "Unnamed option"}
                        </p>

                        <p className="text-xs text-gray-500 mt-1">
                          {normalizedOption.value || "No value"}
                        </p>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* SMALL INFO CARDS */}
        <div className="grid md:grid-cols-3 gap-4 mt-5">
          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <MonitorCog size={18} className="text-green-600" />
              Terminal Setup
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Create POS terminals using terminal name, terminal code and device
              ID.
            </p>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <WalletCards size={18} className="text-blue-600" />
              Drawer Mapping
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Drawers are attached terminal-wise using terminal ID from terminal
              list API.
            </p>
          </div>

          <div className="bg-white border rounded-xl p-4">
            <div className="flex items-center gap-2 text-gray-700 font-medium">
              <PlugZap size={18} className="text-purple-600" />
              POS Device
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Device ID helps connect a terminal or drawer with a physical POS
              device.
            </p>
          </div>
        </div>
      </div>

      {/* TERMINAL FORM MODAL */}
      {terminalModalOpen && (
        <TerminalFormModal
          form={terminalForm}
          setForm={setTerminalForm}
          onClose={() => setTerminalModalOpen(false)}
          onSubmit={handleCreateTerminal}
        />
      )}

      {/* DRAWER FORM MODAL */}
      {drawerModalOpen && selectedTerminal && (
        <DrawerFormModal
          terminal={selectedTerminal}
          form={drawerForm}
          setForm={setDrawerForm}
          onClose={() => setDrawerModalOpen(false)}
          onSubmit={handleCreateDrawer}
        />
      )}

      {/* DETAILS MODAL */}
      {detailsModalOpen && selectedTerminal && (
        <TerminalDetailsModal
          terminal={selectedTerminal}
          drawers={drawers}
          drawerLoading={drawerLoading}
          onClose={() => {
            setDetailsModalOpen(false);
            setDrawers([]);
          }}
          onRefresh={() => fetchDrawersByTerminal(selectedTerminal)}
          onAddDrawer={() => {
            setDetailsModalOpen(false);
            openAddDrawerModal(selectedTerminal);
          }}
        />
      )}
    </>
  );
};

export default Integration;
